import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'gatsby';
import './navbar.styles.scss';
import AimButtonSpotlight from '../buttons/aim-button-spotlight.component';
import { handleBookDemoClick } from './navbar.utils';

const MegaMenu = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const industriesRef = useRef(null);
  const aboutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      setIsScrolled(scrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Use a single handler for leaving the entire mega menu area
  const handleMouseLeave = () => {
    setActiveMenu(null);
  };

  return (
    <div onMouseLeave={handleMouseLeave}>
      {/* Main Navigation Bar with Integrated Dropdowns */}
      <div className="hidden md:block text-white w-full fixed top-0 z-50">
        {/* Background blur layer - extends to cover dropdowns */}
        {(isScrolled || activeMenu) && (
          <div
            className="absolute inset-0 backdrop-blur-xl bg-dark-background/60 -z-10"
            style={{
              height: activeMenu ? 'auto' : '100%',
              minHeight: activeMenu ? '400px' : 'auto'
            }}
          />
        )}

        {/* Navbar content */}
        <div className="relative z-10 px-10 md:px-20">
          <nav className="w-full">
            <div className="w-full flex justify-between">
              <div className="text-lg font-medium flex items-center">
                <Link to="/">
                  <img className="h-6" src="/images/aim-logo_light.svg" alt="AIM Logo" />
                </Link>
              </div>
              <ul className="flex items-center space-x-4">
                {/* AIM360 Link */}
                <li
                  className="flex items-center pt-6 pb-5"
                  onMouseEnter={() => setActiveMenu('aim360')}
                >
                  <span className="fs-psmall flex items-center ml-6 text-white hover:!text-aim-accent transition-colors duration-1000 ease-in-out cursor-default">
                    AIM360
                  </span>
                </li>

                {/* ABOUT US Link */}
                <li
                  ref={aboutRef}
                  className="flex items-center pt-6 pb-5 relative"
                  onMouseEnter={() => setActiveMenu('about')}
                >
                  <span className="fs-psmall flex items-center ml-6 text-white hover:!text-aim-accent transition-colors duration-1000 ease-in-out cursor-default">
                    About
                  </span>
                </li>

                {/* INDUSTRIES Link */}
                <li
                  ref={industriesRef}
                  className="fs-psmall flex items-center pt-6 pb-5 relative"
                  onMouseEnter={() => setActiveMenu('industries')}
                >
                  <span className="fs-psmall flex items-center ml-6 text-white hover:!text-aim-accent transition-colors duration-1000 ease-in-out cursor-default">
                    Industries
                  </span>
                </li>

                {/* BOOK A DEMO BUTTON */}
                <li className="fs-psmall flex items-center">
                  <div className="mx-2 pt-2">
                    <AimButtonSpotlight onClick={handleBookDemoClick} className="fs-psmall">
                      Book a demo
                    </AimButtonSpotlight>
                  </div>
                </li>
              </ul>
            </div>
          </nav>

          {/* Integrated Dropdowns */}
          {activeMenu === 'aim360' && (
            <div className="w-full pt-4 pb-8">
              <div className="flex justify-center items-start py-8 px-16 gap-32">
                {/* Data */}
                <div>
                  <ul className="">
                    <li className="mb-4 text-aim-accent font-medium text-base">
                      <span className="py-1">Data</span>
                    </li>
                    <li className="mb-2">
                      <Link to="/data/core-data/" className="py-1 text-white hover:text-aim-accent">
                        Core Data
                      </Link>
                    </li>
                    <li className="mb-2">
                      <Link to="/data/homemover/" className="py-1 text-white hover:text-aim-accent">
                        Homemover
                      </Link>
                    </li>
                  </ul>
                </div>

                {/* Activation */}
                <div>
                  <ul className="">
                    <li className="mb-4 text-aim-accent font-medium text-base">
                      <span className="py-1">Activation</span>
                    </li>
                    <li className="mb-2">
                      <Link
                        to="/activation/direct-mail/"
                        className="py-1 text-white hover:text-aim-accent"
                      >
                        Direct Mail
                      </Link>
                    </li>
                    <li className="mb-2">
                      <Link
                        to="/activation/acquisition/"
                        className="py-1 text-white hover:text-aim-accent"
                      >
                        Acquisition
                      </Link>
                    </li>
                    <li className="mb-2">
                      <Link
                        to="/activation/retention/"
                        className="py-1 text-white hover:text-aim-accent"
                      >
                        Retention
                      </Link>
                    </li>
                  </ul>
                </div>

                {/* Insights */}
                <div>
                  <ul className="">
                    <li className="mb-4 text-aim-accent font-medium text-base">
                      <span className="py-1">Insights</span>
                    </li>
                    <li className="mb-2">
                      <Link
                        to="/insights/live-campaign-reporting/"
                        className="py-1 text-white hover:text-aim-accent"
                      >
                        Live Campaign Insights
                      </Link>
                    </li>
                    <li className="mb-2">
                      <Link
                        to="/insights/roi-analytics/"
                        className="py-1 text-white hover:text-aim-accent"
                      >
                        ROI Insights
                      </Link>
                    </li>
                    <li className="mb-2">
                      <Link
                        to="/insights/segmentation/"
                        className="py-1 text-white hover:text-aim-accent"
                      >
                        Segmentation
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* About Us Dropdown */}
          {activeMenu === 'about' && (
            <div className="w-full pt-4 pb-8">
              <div
                style={{
                  width: '200px',
                  position: 'absolute',
                  left: aboutRef.current
                    ? `${aboutRef.current.querySelector('span').getBoundingClientRect().left}px`
                    : '0'
                }}
              >
                <ul className="">
                  <li className="mb-2">
                    <Link to="/about/about-us/" className="py-1 text-white hover:text-aim-accent">
                      About Us
                    </Link>
                  </li>
                  <li className="mb-2">
                    <Link to="/about/faq/" className="py-1 text-white hover:text-aim-accent">
                      FAQs
                    </Link>
                  </li>
                  <li className="mb-2">
                    <Link to="/about/why-dm/" className="py-1 text-white hover:text-aim-accent">
                      Why DM?
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Industries Dropdown - Integrated */}
          {activeMenu === 'industries' && (
            <div className="w-full pt-4 pb-8">
              <div
                style={{
                  width: '200px',
                  position: 'absolute',
                  left: industriesRef.current
                    ? `${industriesRef.current.querySelector('span').getBoundingClientRect().left}px`
                    : '0'
                }}
              >
                <ul className="">
                  <li className="mb-2">
                    <Link
                      to="/industries/leisure-travel-cruises/"
                      className="py-1 text-white hover:text-aim-accent"
                    >
                      <span>Travel</span>
                    </Link>
                  </li>
                  <li className="mb-2">
                    <Link
                      to="/industries/property/"
                      className="py-1 text-white hover:text-aim-accent"
                    >
                      Property
                    </Link>
                  </li>
                  <li className="mb-2">
                    <Link
                      to="/industries/utilities/"
                      className="py-1 text-white hover:text-aim-accent"
                    >
                      Utilities
                    </Link>
                  </li>
                  <li className="mb-2">
                    <Link
                      to="/industries/broadband/"
                      className="py-1 text-white hover:text-aim-accent"
                    >
                      Broadband
                    </Link>
                  </li>
                  <li className="mb-2">
                    <Link
                      to="/industries/charities/"
                      className="py-1 text-white hover:text-aim-accent"
                    >
                      Charity
                    </Link>
                  </li>
                  <li className="mb-2">
                    <Link
                      to="/industries/senior-living/"
                      className="py-1 text-white hover:text-aim-accent"
                    >
                      Senior Living
                    </Link>
                  </li>
                  <li className="mb-2">
                    <Link
                      to="/industries/insurance/"
                      className="py-1 text-white hover:text-aim-accent"
                    >
                      Insurance
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MegaMenu;
